/**
 * Personal income tax for the Career Comparison Calculator.
 * Extracted verbatim from CareerComparisonCalculator.tsx on 20-Sep-2026 so it can be unit tested.
 * 2025 rates. Single filer (US). Employee-side payroll taxes included (CPP/EI, FICA).
 */

// US Federal + State Tax + FICA
export const calculateUSTax = (income: number, state: string) => {
  // 2025 US Federal Tax Brackets (Single)
  const federalBrackets = [
    { min: 0, max: 11925, rate: 0.10 },
    { min: 11925, max: 48475, rate: 0.12 },
    { min: 48475, max: 103350, rate: 0.22 },
    { min: 103350, max: 197300, rate: 0.24 },
    { min: 197300, max: 250525, rate: 0.32 },
    { min: 250525, max: 626350, rate: 0.35 },
    { min: 626350, max: Infinity, rate: 0.37 }
  ];

  // 2025 Standard deduction (Single: $15,000)
  const standardDeduction = 15000;
  const taxableIncome = Math.max(0, income - standardDeduction);

  // Calculate federal tax on taxable income
  let federalTax = 0;
  for (const bracket of federalBrackets) {
    if (taxableIncome > bracket.min) {
      const taxableInBracket = Math.min(taxableIncome - bracket.min, bracket.max - bracket.min);
      federalTax += taxableInBracket * bracket.rate;
    }
    if (taxableIncome <= bracket.max) break;
  }

  // State income tax — effective rates by state (2025 estimates)
  // Uses approximate effective rates for median-to-high earners
  const stateEffectiveRates: { [key: string]: number } = {
    'AL': 0.04, 'AK': 0, 'AZ': 0.0334, 'AR': 0.039, 'CA': 0.0725,
    'CO': 0.044, 'CT': 0.055, 'DE': 0.055, 'FL': 0, 'GA': 0.0549,
    'HI': 0.065, 'ID': 0.058, 'IL': 0.0495, 'IN': 0.0305, 'IA': 0.044,
    'KS': 0.052, 'KY': 0.04, 'LA': 0.0425, 'ME': 0.0615, 'MD': 0.055,
    'MA': 0.05, 'MI': 0.0425, 'MN': 0.0635, 'MS': 0.047, 'MO': 0.048,
    'MT': 0.059, 'NE': 0.056, 'NV': 0, 'NH': 0, 'NJ': 0.055,
    'NM': 0.049, 'NY': 0.065, 'NC': 0.045, 'ND': 0.0195, 'OH': 0.035,
    'OK': 0.0425, 'OR': 0.08, 'PA': 0.0307, 'RI': 0.0525, 'SC': 0.05,
    'SD': 0, 'TN': 0, 'TX': 0, 'UT': 0.0465, 'VT': 0.058,
    'VA': 0.0535, 'WA': 0, 'WV': 0.052, 'WI': 0.053, 'WY': 0,
    'DC': 0.065
  };
  const stateRate = stateEffectiveRates[state] || 0;
  const stateTax = taxableIncome * stateRate;

  // FICA taxes (2025)
  // Social Security: 6.2% up to $176,100
  const ssWageBase = 176100;
  const ssTax = Math.min(income, ssWageBase) * 0.062;

  // Medicare: 1.45% (no cap) + 0.9% additional above $200,000
  let medicareTax = income * 0.0145;
  if (income > 200000) {
    medicareTax += (income - 200000) * 0.009;
  }

  const totalTax = federalTax + stateTax + ssTax + medicareTax;
  return Math.max(0, totalTax);
};


// 2025 CPP: 5.95% from $3,500 to $71,300, then CPP2 at 4% from $71,300 to $81,200
export const calculateCpp = (income: number): number => {
  const tier1 = Math.min(Math.max(0, income - 3500), 71300 - 3500) * 0.0595;
  const tier2 = income > 71300 ? Math.min(income - 71300, 81200 - 71300) * 0.04 : 0;
  return tier1 + tier2;
};

// 2025 EI: 1.64% up to $65,700 of insurable earnings
export const calculateEi = (income: number): number => Math.min(income, 65700) * 0.0164;

// 2025 Canada Employment Amount
export const CANADA_EMPLOYMENT_AMOUNT = 1471;

// 2025 federal BPA: $16,129, reduced in a straight line to $14,538 between the 4th and 5th bracket thresholds
export const federalBasicPersonalAmount = (income: number): number => {
  if (income <= 177882) return 16129;
  if (income >= 253414) return 14538;
  return 16129 - (16129 - 14538) * (income - 177882) / (253414 - 177882);
};

// Ontario surtax (2025): 20% of Ontario tax over $5,710 plus 36% of Ontario tax over $7,307
export const ontarioSurtax = (ontarioTaxAfterCredits: number): number =>
  Math.max(0, ontarioTaxAfterCredits - 5710) * 0.20 + Math.max(0, ontarioTaxAfterCredits - 7307) * 0.36;

// Ontario Health Premium: stepped schedule, $0 under $20,000 up to $900 above $200,600
export const ontarioHealthPremium = (income: number): number => {
  if (income <= 20000) return 0;
  if (income <= 36000) return Math.min(300, (income - 20000) * 0.06);
  if (income <= 48000) return Math.min(450, 300 + (income - 36000) * 0.06);
  if (income <= 72000) return Math.min(600, 450 + (income - 48000) * 0.25);
  if (income <= 200000) return Math.min(750, 600 + (income - 72000) * 0.25);
  return Math.min(900, 750 + (income - 200000) * 0.25);
};

export interface CanadianTaxBreakdown {
  federal: number;
  provincial: number;
  cpp: number;
  ei: number;
  total: number;
}

// Canadian Federal + Provincial Tax + CPP + EI
export const canadianTaxBreakdown = (income: number, province: string): CanadianTaxBreakdown => {
  // 2025 Federal Tax Brackets
  const federalBrackets = [
    { min: 0, max: 57375, rate: 0.15 },
    { min: 57375, max: 114750, rate: 0.205 },
    { min: 114750, max: 177882, rate: 0.26 },
    { min: 177882, max: 253414, rate: 0.29 },
    { min: 253414, max: Infinity, rate: 0.33 }
  ];

  // 2025 Provincial Tax Brackets
  const provincialBrackets: { [key: string]: Array<{ min: number; max: number; rate: number }> } = {
    'Alberta': [
      { min: 0, max: 60000, rate: 0.08 },
      { min: 60000, max: 151234, rate: 0.10 },
      { min: 151234, max: 181481, rate: 0.12 },
      { min: 181481, max: 241974, rate: 0.13 },
      { min: 241974, max: 362962, rate: 0.14 },
      { min: 362962, max: Infinity, rate: 0.15 }
    ],
    'British Columbia': [
      { min: 0, max: 49279, rate: 0.0506 },
      { min: 49279, max: 98560, rate: 0.077 },
      { min: 98560, max: 113158, rate: 0.105 },
      { min: 113158, max: 137407, rate: 0.1229 },
      { min: 137407, max: 186306, rate: 0.147 },
      { min: 186306, max: 259829, rate: 0.168 },
      { min: 259829, max: Infinity, rate: 0.205 }
    ],
    'Manitoba': [
      { min: 0, max: 47564, rate: 0.108 },
      { min: 47564, max: 101200, rate: 0.1275 },
      { min: 101200, max: Infinity, rate: 0.174 }
    ],
    'New Brunswick': [
      { min: 0, max: 51306, rate: 0.094 },
      { min: 51306, max: 102614, rate: 0.14 },
      { min: 102614, max: 190060, rate: 0.16 },
      { min: 190060, max: Infinity, rate: 0.195 }
    ],
    'Newfoundland and Labrador': [
      { min: 0, max: 44192, rate: 0.087 },
      { min: 44192, max: 88382, rate: 0.145 },
      { min: 88382, max: 157792, rate: 0.158 },
      { min: 157792, max: 220910, rate: 0.178 },
      { min: 220910, max: 282214, rate: 0.198 },
      { min: 282214, max: 564429, rate: 0.208 },
      { min: 564429, max: 1128858, rate: 0.213 },
      { min: 1128858, max: Infinity, rate: 0.218 }
    ],
    'Northwest Territories': [
      { min: 0, max: 51964, rate: 0.059 },
      { min: 51964, max: 103930, rate: 0.086 },
      { min: 103930, max: 169080, rate: 0.122 },
      { min: 169080, max: Infinity, rate: 0.1405 }
    ],
    'Nova Scotia': [
      { min: 0, max: 29590, rate: 0.0879 },
      { min: 29590, max: 59180, rate: 0.1495 },
      { min: 59180, max: 93000, rate: 0.1667 },
      { min: 93000, max: 150000, rate: 0.175 },
      { min: 150000, max: Infinity, rate: 0.21 }
    ],
    'Nunavut': [
      { min: 0, max: 54707, rate: 0.04 },
      { min: 54707, max: 109413, rate: 0.07 },
      { min: 109413, max: 177882, rate: 0.09 },
      { min: 177882, max: Infinity, rate: 0.115 }
    ],
    'Ontario': [
      { min: 0, max: 52886, rate: 0.0505 },
      { min: 52886, max: 105775, rate: 0.0915 },
      { min: 105775, max: 150000, rate: 0.1116 },
      { min: 150000, max: 220000, rate: 0.1216 },
      { min: 220000, max: Infinity, rate: 0.1316 }
    ],
    'Prince Edward Island': [
      { min: 0, max: 33328, rate: 0.098 },
      { min: 33328, max: 64656, rate: 0.138 },
      { min: 64656, max: 105000, rate: 0.167 },
      { min: 105000, max: Infinity, rate: 0.187 }
    ],
    'Quebec': [
      { min: 0, max: 53255, rate: 0.14 },
      { min: 53255, max: 106495, rate: 0.19 },
      { min: 106495, max: 129590, rate: 0.24 },
      { min: 129590, max: Infinity, rate: 0.2575 }
    ],
    'Saskatchewan': [
      { min: 0, max: 53463, rate: 0.105 },
      { min: 53463, max: 152750, rate: 0.125 },
      { min: 152750, max: Infinity, rate: 0.145 }
    ],
    'Yukon': [
      { min: 0, max: 57375, rate: 0.064 },
      { min: 57375, max: 114750, rate: 0.09 },
      { min: 114750, max: 177882, rate: 0.109 },
      { min: 177882, max: 500000, rate: 0.128 },
      { min: 500000, max: Infinity, rate: 0.15 }
    ]
  };

  // 2025 Provincial Basic Personal Amounts (BPA × lowest bracket rate = credit)
  const provincialBPA: { [key: string]: { amount: number; rate: number } } = {
    'Alberta':                    { amount: 22323, rate: 0.08 },
    'British Columbia':           { amount: 12932, rate: 0.0506 },
    'Manitoba':                   { amount: 15969, rate: 0.108 },
    'New Brunswick':              { amount: 13396, rate: 0.094 },
    'Newfoundland and Labrador':  { amount: 10818, rate: 0.087 },
    'Northwest Territories':      { amount: 17373, rate: 0.059 },
    'Nova Scotia':                { amount: 8481,  rate: 0.0879 },
    'Nunavut':                    { amount: 18767, rate: 0.04 },
    'Ontario':                    { amount: 12747, rate: 0.0505 },
    'Prince Edward Island':       { amount: 13500, rate: 0.098 },
    'Quebec':                     { amount: 18571, rate: 0.14 },
    'Saskatchewan':               { amount: 18491, rate: 0.105 },
    'Yukon':                      { amount: 16129, rate: 0.064 },
  };

  const bracketTax = (brackets: Array<{ min: number; max: number; rate: number }>): number => {
    let tax = 0;
    for (const bracket of brackets) {
      if (income > bracket.min) {
        tax += Math.min(income - bracket.min, bracket.max - bracket.min) * bracket.rate;
      }
      if (income <= bracket.max) break;
    }
    return tax;
  };
  // An unrecognised province is treated as Ontario throughout, surtax and health premium included.
  const resolvedProvince = provincialBrackets[province] ? province : 'Ontario';
  const federalTax = bracketTax(federalBrackets);
  const provincialTax = bracketTax(provincialBrackets[resolvedProvince]);

  const cppContribution = calculateCpp(income);
  const eiContribution = calculateEi(income);

  // Federal non-refundable credits at 15%: BPA, Canada Employment Amount, employee CPP/EI
  const federalCredits = (federalBasicPersonalAmount(income) + CANADA_EMPLOYMENT_AMOUNT + cppContribution + eiContribution) * 0.15;
  const federalTaxPayable = Math.max(0, federalTax - federalCredits);

  // Provincial non-refundable credits at the lowest provincial rate: BPA, employee CPP/EI
  const provBPA = provincialBPA[resolvedProvince];
  const provincialCredits = (provBPA.amount + cppContribution + eiContribution) * provBPA.rate;
  let provincialTaxPayable = Math.max(0, provincialTax - provincialCredits);
  if (resolvedProvince === 'Ontario') {
    provincialTaxPayable += ontarioSurtax(provincialTaxPayable) + ontarioHealthPremium(income);
  }
  // Quebec is approximate: no 16.5% federal abatement, CPP/EI stand in for QPP/QPIP.

  return {
    federal: federalTaxPayable,
    provincial: provincialTaxPayable,
    cpp: cppContribution,
    ei: eiContribution,
    total: federalTaxPayable + provincialTaxPayable + cppContribution + eiContribution
  };
};

export const calculateCanadianTax = (income: number, province: string): number =>
  canadianTaxBreakdown(income, province).total;
